import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Checkbox } from 'antd';

export default class PropertySelectorModal extends React.Component {
  constructor(props) {
    super(props);

    const { properties, propertiesSelected, propertiesSlugsExcluded } = props;
    this.state = {
      properties: this.initProperties(properties, propertiesSelected, propertiesSlugsExcluded),
      searchText: '',
    };
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.loading !== this.props.loading) {
      const { properties, propertiesSelected, propertiesSlugsExcluded } = nextProps;
      this.setState({
        properties: this.initProperties(properties, propertiesSelected, propertiesSlugsExcluded),
      });
    }
  }

  handleSearchChange = e => {
    this.setState({
      searchText: e.target.value,
    });
  };

  initProperties = (properties, propertiesSelected, propertiesSlugsExcluded) => {
    if (!properties) return [];
    const result = {};
    Object.keys(properties).forEach(countryName => {
      result[countryName] = properties[countryName].map((property, index) => {
        const selected = !!propertiesSelected.find(item => item.slug === property.slug);
        const disabled = propertiesSlugsExcluded.includes(property.slug);
        return {
          ...property,
          index,
          selected,
          disabled,
        };
      });
    });
    return result;
  };

  handleSelectCountry = (selected, countryName, countryProperties) => {
    const { properties } = this.state;
    countryProperties.forEach(property => {
      if (!property.disabled) {
        properties[countryName][property.index].selected = selected;
      }
    });
    this.setState({
      properties,
    });
  };

  handleSelect = (country, index) => {
    const { properties } = this.state;
    properties[country][index].selected = !properties[country][index].selected;
    const selectAll = !properties[country][index].selected ? false : this.state.selectAll;
    this.setState({
      properties,
      selectAll,
    });
  };

  getSelectedCountry = () => {
    if (this.props.onlyOneCountry) {
      // eslint-disable-next-line
      for (const country in this.state.properties) {
        if (this.state.properties[country].find(property => property.selected)) {
          return country;
        }
      }
      return null;
    }
    return null;
  };

  handleConfirm = () => {
    const selectedProperties = [];
    Object.keys(this.state.properties).forEach(country => {
      this.state.properties[country].forEach(property => {
        if (property.selected && !property.disabled) {
          selectedProperties.push({
            name: property.name,
            slug: property.slug,
            id: property.id,
            currency: property.currency,
            country: property.city.country,
            status: property.status,
            bookingJourney: property.bookingJourney,
          });
        }
      });
    });
    this.props.onSubmit(selectedProperties);
  };

  getMatchedProperty = () => {
    const searchText = this.state.searchText.trim();
    const country = this.getSelectedCountry();
    const matchedProperties = {};
    Object.keys(this.state.properties).forEach(countryName => {
      matchedProperties[countryName] = {};
      let hasPropertyUnselected = false;
      let hasPropertyEnabled = false;
      matchedProperties[countryName].properties = [];
      const isCountryDisabled = country && country !== countryName;
      this.state.properties[countryName].forEach(property => {
        if (
          !searchText ||
          (property.name + property.slug).toLowerCase().includes(searchText.toLowerCase())
        ) {
          if (!property.disabled) {
            hasPropertyEnabled = true;
          }

          if (!property.disabled && !property.selected) {
            hasPropertyUnselected = true;
          }
          matchedProperties[countryName].properties.push(
            Object.assign({}, property, {
              disabled:
                isCountryDisabled || property?.status === 'PUBLISHED' ? true : property.disabled,
            }),
          );
        }
      });
      matchedProperties[countryName].disabled = isCountryDisabled;
      matchedProperties[countryName].selected = hasPropertyEnabled && !hasPropertyUnselected;
    });
    return matchedProperties;
  };

  render() {
    const { t, loading, isOnTop } = this.props;
    const matchedProperties = this.getMatchedProperty();
    return (
      <div
        className={classNames('property-selector-modal', {
          'property-selector-modal--top': isOnTop,
        })}
      >
        <div className="property-selector-modal__search">
          <Input
            placeholder={t('landlord.select_property.search')}
            suffix={<SearchOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
            onChange={this.handleSearchChange}
          />
        </div>
        <div className="property-selector-modal__list">
          <Choose>
            <When condition={loading}>
              <div className="property-selector-modal__loading" />
            </When>
            <Otherwise>
              <Choose>
                <When condition={matchedProperties && Object.keys(matchedProperties).length > 0}>
                  <For each="country" of={Object.keys(matchedProperties)} index="countryIndex">
                    <If condition={matchedProperties[country].properties.length > 0}>
                      <div className="property-selector-modal__country-topic" key={country}>
                        <div className="property-selector-modal__country">
                          <Checkbox
                            className="property-selector-modal__country__label"
                            checked={matchedProperties[country].selected}
                            disabled={matchedProperties[country].disabled}
                            onChange={() => {
                              this.handleSelectCountry(
                                !matchedProperties[country].selected,
                                country,
                                matchedProperties[country].properties,
                              );
                            }}
                          >
                            {country}
                          </Checkbox>
                        </div>
                        <For each="property" of={matchedProperties[country].properties} index="idx">
                          <Checkbox
                            className="property-selector-modal__item"
                            checked={property.selected}
                            disabled={property.disabled}
                            onChange={() => {
                              this.handleSelect(country, property.index);
                            }}
                            key={property.slug}
                          >
                            {property.name}
                          </Checkbox>
                        </For>
                      </div>
                    </If>
                  </For>
                </When>
                <Otherwise>
                  <div className="property-selector-modal__no-result">
                    {t('landlord.select_property.no_result')}
                  </div>
                </Otherwise>
              </Choose>
            </Otherwise>
          </Choose>
        </div>
        <div className="property-selector-modal__footer">
          <If condition={this.state.properties && Object.keys(this.state.properties).length > 0}>
            <Button size="small" type="primary" onClick={this.handleConfirm}>
              {t('landlord.select_property.confirm')}
            </Button>
          </If>
        </div>
      </div>
    );
  }
}

PropertySelectorModal.propTypes = {
  properties: PropTypes.object,
  propertiesSelected: PropTypes.array,
  propertiesSlugsExcluded: PropTypes.array,
  loading: PropTypes.bool,
  t: PropTypes.func,
  isOnTop: PropTypes.bool,
  onSubmit: PropTypes.func,
  onlyOneCountry: PropTypes.bool,
};

PropertySelectorModal.defaultProps = {
  t: () => {},
  properties: null,
  propertiesSelected: [],
  propertiesSlugsExcluded: [],
  loading: false,
  isOnTop: false,
  onSubmit: () => {},
  onlyOneCountry: true,
};
